import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import WikiItemGrid from './components/WikiItemGrid';
import WikiDetailPanel from './components/WikiDetailPanel';
import WikiBreadcrumbs from './components/WikiBreadcrumbs';
import { parseWikiUrl } from '../../utils/wikiUrlParser';
import GlassContainer from '../../components/layout/GlassContainer';
import { BORDER_COLORS } from '../../utils/colors';


const WikiCategoryPage: React.FC = () => {
  const location = useLocation();
  const isSidebarVisible = useMediaQuery('(min-width: 870px)');

  const [previewItem, setPreviewItem] = useState<{
    itemId: number;
    detailUrl: string;
    forPathname: string;
  } | null>(null);

  // Parse RESTful URL structure
  const urlParams = parseWikiUrl(location.pathname);

  const handlePreview = useCallback((itemId: number, detailUrl: string) => {
    setPreviewItem({ itemId, detailUrl, forPathname: location.pathname });
  }, [location.pathname]);

  const handleClosePreview = useCallback(() => {
    setPreviewItem(null);
  }, []);

  // Synchronous check: preview is only valid for the pathname it was opened on
  // Also disable preview on mobile devices where sidebar is hidden (< 870px)
  const isPreviewOpen = isSidebarVisible && previewItem !== null && previewItem.forPathname === location.pathname;

  if (!urlParams) {
    return (
      <GlassContainer>
        <Box sx={{ color: 'white' }}>
          <div>Invalid wiki URL</div>
        </Box>
      </GlassContainer>
    );
  }

  return (
    <GlassContainer sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: isPreviewOpen ? 2 : 0 }}>
        {/* Left: Grid */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          color: 'white',
        }}>
          <WikiBreadcrumbs />
          <WikiItemGrid
            source={urlParams.source}
            typeId={urlParams.typeId}
            subtypeId={urlParams.subtypeId}
            wikiSubCategory={urlParams.subcategoryId}
            raceId={urlParams.raceId}
            skillTypeId={urlParams.skillTypeId}
            classId={urlParams.classId}
            professionId={urlParams.professionId}
            onPreview={isSidebarVisible ? handlePreview : undefined}
            isPreviewOpen={isPreviewOpen}
            selectedItemId={isPreviewOpen && previewItem ? previewItem.itemId : null}
          />
        </Box>

        {/* Divider + Detail panel */}
        {isPreviewOpen && previewItem && (
          <>
            <Box sx={{
              width: '1px',
              background: BORDER_COLORS.DEFAULT,
              flexShrink: 0,
            }} />
            <Box sx={{
              flex: { xs: '1', sm: '0 0 40%', md: '0 0 30%', lg: '0 0 35%', xl: '0 0 35%' },
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}>
              <WikiDetailPanel
                source={urlParams.source}
                itemId={previewItem.itemId}
                detailUrl={previewItem.detailUrl}
                onClose={handleClosePreview}
                raceId={urlParams.raceId}
                skillTypeId={urlParams.skillTypeId}
                classId={urlParams.classId}
                professionId={urlParams.professionId}
              />
            </Box>
          </>
        )}
      </Box>
    </GlassContainer>
  );
};

export default WikiCategoryPage;
